<script setup>
import { useMetrics } from "@/composables/use.metrics";
import { copyTextToClipboard } from "@/lib/copy.text.to.clipboard";
import { errorHandler } from "@/lib/errorHandler";
import { computed, defineEmits, defineProps, reactive, ref, watch } from "vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
});

const emits = defineEmits(["update:modelValue", "next"]);

function onClose() {
  emits('update:modelValue', false);
}

const worldWarTwoQuiz = [
  {
    question: "Какое событие официально считается началом Второй мировой войны?",
    answers: [
      "Нападение Германии на Польшу",
      "Высадка союзников в Нормандии",
      "Оскорбление Гитлером Франца Фердинанда",
      "Нападение Германии на СССР"
    ],
    correctAnswer: 0
  },
  {
    question: "Какая битва считается крупнейшим танковым сражением Второй мировой войны?",
    answers: [
      "Битва за Эль-Аламейн",
      "Курская битва",
      "Битва за Атлантику",
      "Перл Харбор"
    ],
    correctAnswer: 1
  },
  {
    question: "Как назывался план Германии по нападению на СССР?",
    answers: [
      "Операция «Морской лев»",
      "Операция «Цитадель»",
      "Операция «Цигель»",
      "Операция «Барбаросса»"
    ],
    correctAnswer: 3
  },
  {
    question: "Какое государство оставалось нейтральным на протяжении всей войны?",
    answers: [
      "Швейцария",
      "Франция",
      "Таиланд",
      "Бразилия"
    ],
    correctAnswer: 0
  },
  {
    question: "Какой город подвергся первой в истории атомной бомбардировке?",
    answers: [
      "Токио",
      "Берлин",
      "Хиросима",
      "Спрингфилд"
    ],
    correctAnswer: 2
  },
  {
    question: "Какой советский маршал принял капитуляцию Германии в 1945 году?",
    answers: [
      "Георгий Жуков",
      "Константин Рокоссовский",
      "Иван Конев",
      "Пётр Багратион"
    ],
    correctAnswer: 0
  },
  {
    question: "Как называлась операция по высадке союзников в Нормандии 6 июня 1944 года?",
    answers: [
      "Операция «Сюрприз»",
      "Операция «Шторм»",
      "Операция «Оверлорд»",
      "Операция «Марс»"
    ],
    correctAnswer: 2
  },
  {
    question: "Как называлось самое мощное немецкое супероружие, так и не вошедшее в массовое производство?",
    answers: [
      "«Маус» (сверхтяжёлый танк)",
      "«Фау-2» (баллистическая ракета)",
      "«Вундер вафел» (гаубица)",
      "«Т-т-т» (автомат)"
    ],
    correctAnswer: 0
  },
  {
    question: "Какой город был столицей фашистской Италии во время Второй мировой войны?",
    answers: [
      "Мордор",
      "Берлин",
      "Париж",
      "Рим"
    ],
    correctAnswer: 3
  },
  {
    question: "Какой флаг был водружён над Рейхстагом в мае 1945 года?",
    answers: [
      "Флаг СССР",
      "Флаг США",
      "Флаг Великобритании",
      "Белый флаг"
    ],
    correctAnswer: 0
  }
];


const { eventTypes, hitType, hit } = useMetrics();

const words = ref([]);

const currentQuestionIndex = ref(0);

const answers = reactive({});

const itIsFinal = computed(() => currentQuestionIndex.value + 1 === words.value.length + 1);

function nextQuestion() {
  if (itIsFinal.value) {
    emits("next");
    hit(eventTypes.FIREWORK, hitType.QUIZ, {
      finalText: finalText.value,
      rightAnswersCount: rightAnswersCount.value,
      totalQuestions: words.value.length,
    })
    return
  }
  currentQuestionIndex.value += 1;
}

const rightAnswersCount = computed(() => Object.values(answers).filter((answer, index) => answer === words.value[index]?.correctAnswer).length)

const finalText = computed(() => {
  if (rightAnswersCount.value < 2) {
    return 'Кажется, ваши знания по истории стоит освежить! Вы создали свой праздничный салют! Отправьте его вашим близким!'
  }

  return 'Поздравляем! Вы создали свой праздничный салют! Отправьте его вашим близким!'
});

function getRandomUniqueElements(arr) {
  if (arr.length < 3) {
    return arr;
  }

  const copy = [...arr];
  const result = [];

  for (let i = 0; i < 3; i++) {
    const randomIndex = Math.floor(Math.random() * copy.length);
    result.push(copy.splice(randomIndex, 1)[0]);
  }

  return result;
}

const loadingShare = ref(false);
async function onShareVideo() {
  const shareData = {
    title: "С Днём Победы!",
    url: localStorage.getItem('video'),
  }
  try {
    loadingShare.value = true;
    hit(eventTypes.FIREWORK, hitType.SHARE, shareData);
    copyTextToClipboard(shareData.url).finally(() => errorHandler({ name: 'copy' }))
    if (navigator.share) {
      await navigator.share(shareData);
    }
  } finally {
    loadingShare.value = false;
  }
}

const v = ref('');

watch(itIsFinal, (state) => {
  if (state) {
    v.value = localStorage.getItem('video');
    copyTextToClipboard(v.value);
  }
});

watch(() => props.modelValue, (state) => {
  document.body.style.overflowY = state ? 'hidden' : 'visible';
  words.value = state ? getRandomUniqueElements(worldWarTwoQuiz) : [];
}, { immediate: true });
</script>

<template>
  <Transition name="fade">
    <Transition name="fade">
      <div v-if="modelValue" class="shell" :class="{ shell_overflow: itIsFinal }">
        <div class="modal">
          <div class="modal-content">
            <div class="caption"  v-if="!itIsFinal">
              <div class="caption-left">
                <p class="title fw-7">
                  Пройдите квиз для получения ссылки на скачивание видео
                </p>
              </div>
              <button class="close" @click="onClose">
                <svg class="close__img" width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_7509_946)">
                    <rect width="21" height="21" rx="10.5" fill="white" />
                    <path d="M10.5 0C4.65 0 0 4.65 0 10.5C0 16.35 4.65 21 10.5 21C16.35 21 21 16.35 21 10.5C21 4.65 16.35 0 10.5 0ZM14.55 15.75L10.5 11.7L6.45 15.75L5.25 14.55L9.3 10.5L5.25 6.45L6.45 5.25L10.5 9.3L14.55 5.25L15.75 6.45L11.7 10.5L15.75 14.55L14.55 15.75Z" fill="#5D6C85" />
                  </g>
                  <defs>
                    <clipPath id="clip0_7509_946">
                      <rect width="21" height="21" rx="10.5" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
            <!-- TODO Сам квиз -->
            <div class="quiz" v-if="words[currentQuestionIndex]">
              <div class="quiz-head">
                <p class="quiz-title">{{ words[currentQuestionIndex].question }}</p>
                <p class="quiz-count">Вопрос {{ currentQuestionIndex + 1 }} из {{ words.length }}</p>
              </div>
              <Transition name="fade" mode="out-in">
                <div :key="currentQuestionIndex" class="quiz-content">
                  <label
                    v-for="(answer, index) in words[currentQuestionIndex].answers"
                    :key="index"
                    class="quiz-element"
                    :class="{
                      'quiz-element--selected': answers[currentQuestionIndex] === index && [currentQuestionIndex]?.correctAnswer !== answers[currentQuestionIndex],
                      'quiz-element--correct':  answers[currentQuestionIndex] !== undefined && words[currentQuestionIndex]?.correctAnswer === index
                    }"
                  >
                    <input
                      type="radio"
                      v-model="answers[currentQuestionIndex]"
                      :disabled="answers[currentQuestionIndex] !== undefined"
                      name="question"
                      class="quiz-element__input"
                      :value="index"
                    />
                    <div class="quiz-element__bg"></div>
                    <p class="quiz-element__count">{{ index + 1 }}</p>
                    <p class="quiz-element__text">{{ answer }}</p>
                  </label>
                </div>
              </Transition>
            </div>
            <!-- TODO Итог -->
            <div v-if="itIsFinal" class="final">
              <!-- <p class="final__title">Заголовок</p> -->
              <p class="final__text">{{ finalText }}</p>
              <!-- TODO -->
              <div class="final-end">
                <input placeholder="" class="final__input" readonly :value="v">
                <p class="final__subtitle final__subtitle-2">Напишите поздравление с Днём Победы своим близким  и  дополните его ссылкой на видеопоздравление!</p>
              </div>
            </div>
            <div class="quiz-actions">
              <button
                v-if="itIsFinal || loadingShare"
                class="link"
                @click="onShareVideo"
              >
                Поделиться
              </button>
              <button
                :disabled="currentQuestionIndex in answers && answers[currentQuestionIndex] === undefined"
                class="link"
                @click="nextQuestion"
              >
                Дальше
            </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Transition>
</template>

<style scoped lang="scss">
.shell {
  z-index: 1000000;
  background: #00288c;
  &_overflow {
    overflow: hidden;
  }
}

.modal {
  padding: 0;
  max-width: 872px;
  border-radius: 40px;
  pointer-events: all;
  backdrop-filter: blur(8px);
  background: rgba(157, 196, 248, 0.3);
  padding: 24px;
  width: 100%;
  overflow-y: auto;

  &-content{
    background: #fff;

    border-radius: 24px;
    padding: 24px;

  }

  @media (max-width:1000px) {
    padding: 16px;
      max-width: 742px;
    &-content {
      padding: 16px;
    }
  }
}

.title {
  font-weight: 700;
  font-size: 20px;
  line-height: 125%;
  letter-spacing: 0.03em;
  color: #000;
}
.text {
  margin-top: 8px;
  font-size: 14px;
  line-height: 129%;
  letter-spacing: 0.03em;
  color: rgba(0, 0, 0, 0.5);
}

.caption {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.close {
  display: flex;
  background: none;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  transition: all 0.5s ease-in-out;

  &:hover {
    opacity: 0.5;
  }

  img {
    width: 100%;
  }
}

.quiz {
  margin-top: 16px;
  &-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 12px;
  }
  &-title {
    font-weight: 700;
    font-size: 36px;
    line-height: 130%;
    letter-spacing: 0.01em;
    color: #000;
  }
  &-count {
    font-weight: 500;
    font-size: 16px;
    line-height: 130%;
    letter-spacing: 0.01em;
    color: #a5c3f0;
    margin-bottom: 6px;
  }
  &-content {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
  &-element {
    display: flex;
    align-items: center;
    position: relative;
    padding: 17px 32px;
    border-radius: 24px;
    gap: 15px;
    cursor: pointer;
    transition: all 0.5s ease-in-out;
    &:hover {
      opacity: 0.5;
    }
    &__bg {
      position: absolute;
      width: 100%;
      height: 100%;
      background: #e7f0fd;
      border-radius: 24px;
      left: 0;
      z-index: 0;
      transition: all 0.2s ease-in-out;
    }
    &__input {
      margin-top: 0;
      position: absolute;
      width: 100%;
      height: 100%;
      cursor: pointer;
      border-radius: 24px;
      left: 0;
    }
    &__count {
      position: relative;
      z-index: 1;
      font-weight: 700;
      font-size: 24px;
      line-height: 130%;
      letter-spacing: 0.01em;
      color: #374044;
    }
    &__text {
      position: relative;
      z-index: 1;
      font-weight: 500;
      font-size: 20px;
      line-height: 130%;
      letter-spacing: 0.01em;
      color: #374044;
    }

    &--selected &__bg {
      background: #ea6511 !important;
    }

    &--correct &__bg {
      background: #07db71 !important;
    }
  }

  &-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  @media (max-width:1000px) {
    &-title{
      font-size: 18px;
    }
    &-actions{
      margin-top: 20px;
      gap: 12px;
      .link{
        margin-top: 0;
      }
    }
    &-element{
      padding: 12px 16px;
      &__count{
        font-size: 16px;
      }
      &__text{
        font-size: 16px;
      }
    }
  }
}
.final {
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  &__title {
    font-weight: 700;
    font-size: 64px;
    line-height: 130%;
    letter-spacing: 0.01em;
    text-align: center;
    color: #000;
  }
  &__text {
    margin-top: 3px;
    font-weight: 700;
    font-size: 24px;
    line-height: 130%;
    letter-spacing: 0.01em;
    text-align: left;
    color: #000;
  }
  &-end{
    margin-top: 40px;
  }
  &__subtitle{
    font-size: 24px;
    line-height: 130%;
    letter-spacing: 0.01em;
    text-align: left;
    color: #000;
    font-weight: 500;
    a{
      color: #d72d41;
    }
    &__input{
      margin-top: 22px;
    }
    &-2{
      margin-top: 22px;
      font-size: 20px;
    }
  }
  @media (max-width:1000px) {
  &__title {
    font-size: 24px;
  }
  &__text {
    font-size: 18px;
  }
  &-end{
    margin-top: 24px;
  }
  &__subtitle{
    font-size: 20px;
    &-2{
      margin-top: 22px;
      font-size: 16px;
    }
  }
  }
}
.link {
  justify-content: center;
  margin-top: 16px;
  width: 100%;
  padding: 13px;
  font-size: 16px;
  line-height: 140%;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

@media (max-width: 1300px) {
  .title {
    font-size: 18px;
  }
  .text {
    font-size: 16px;
  }
}
</style>
